'use client';

/**
 * "Personalize your search" pill — home-page entry point into the
 * Renter Profile wizard.
 *
 * Visibility rules (per PRD):
 *   - Logged out                                       → show pill, opens auth modal first
 *   - Logged in && !isRenterProfileCompleted           → show pill, opens wizard directly
 *   - Logged in &&  isRenterProfileCompleted           → hide pill
 *
 * Auth gating (nuance): after signup the app can push the user into
 * a phone-verification step INSIDE the same auth modal. We must not
 * open the wizard until that whole modal chain is closed — otherwise
 * the wizard renders on top of verify-phone. Rather than await the
 * ``openAuthFlow()`` promise (which resolves per-step and can race
 * with follow-up steps), we track a "pending" intent flag and open
 * the wizard from a store-watch effect when ALL of:
 *   - user clicked the pill (intent set)
 *   - user is now logged in
 *   - auth UI has finished (isOpen === false)
 * are true. This holds correctly regardless of how many sub-steps
 * the auth modal walks through.
 */

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { IconHeart, IconChevronRight } from '@repo/svgs';
import { useStore } from '@/providers/StoreProvider';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useListingAlert } from '@/app/saved/hooks/useListingAlert';
import { cn } from '@/lib/cn';
import { RenterProfileWizardModal } from '@/components/preferences/RenterProfileWizardModal';

export function PersonalizePill() {
  // Renter profile is rent-side only — never show on the reviews tab.
  const isReviews = usePathname() === '/read-reviews';
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const authUIOpen = useStore(s => s.authUI.isOpen);
  const { openAuthFlow } = useAuthModal();
  const analytics = useAnalytics();
  const { data: profile, loading } = useListingAlert();
  const [wizardOpen, setWizardOpen] = useState(false);
  // "User pressed pill and is waiting for auth to finish". Latches
  // until either the wizard actually opens or the user cancels auth.
  const [pendingWizardOpen, setPendingWizardOpen] = useState(false);

  // Hide-by-default visibility rule. The pill only renders once we've
  // confirmed the API state — either the user is logged out (show)
  // or logged in with `isRenterProfileCompleted !== true` (show).
  // Previously this was flipped (show while loading, hide once
  // completed=true arrived), which caused a one-frame flash of the
  // pill for returning completed users. The trade-off here is a
  // small delay before the pill appears for uncompleted users while
  // the API resolves — a much less jarring UX than the flash.
  const hidden = isLoggedIn && (loading || profile?.isRenterProfileCompleted === true);

  const onClick = useCallback(() => {
    analytics.track('home_personalize');
    if (!isLoggedIn) {
      // Latch the intent. The effect below is the single owner of
      // "when do we actually open the wizard" — it only fires once
      // the auth modal (and any nested verify-phone step) is fully
      // closed AND the session is authenticated.
      setPendingWizardOpen(true);
      openAuthFlow({
        title: 'Log in or sign up for personalization',
        description: 'Log in or sign up to personalize your apartment search.',
      }).catch(() => {
        // User cancelled — drop the pending intent so we don't fire
        // the wizard on a stale flag if they sign in later via a
        // different surface (e.g. header login button).
        setPendingWizardOpen(false);
      });
      return;
    }
    // Already logged in — open the wizard immediately. No auth-UI
    // to wait for.
    setWizardOpen(true);
  }, [analytics, isLoggedIn, openAuthFlow]);

  useEffect(() => {
    // Deferred-open guard: fire the wizard ONLY when the auth flow
    // has finished (isOpen === false) AND we're logged in AND the
    // user actually asked for the wizard (pendingWizardOpen).
    // Handles phone-verify, signup-details, and any other future
    // sub-step because those all live inside authUI.isOpen.
    if (pendingWizardOpen && isLoggedIn && !authUIOpen) {
      setPendingWizardOpen(false);
      setWizardOpen(true);
    }
  }, [pendingWizardOpen, isLoggedIn, authUIOpen]);

  if (hidden || isReviews) return null;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          // Layout & size — spec is 12px horizontal + 8px vertical
          // padding, 8px gap between icon → text → chevron.
          'inline-flex items-center gap-2 px-3 py-2 rounded-full',
          // Appearance — pill sits on the hero (bg-secondary) so we
          // give it the primary background + secondary border + the
          // two-stop drop shadow from the Figma spec.
          'bg-primary border border-secondary',
          'shadow-[0_2px_4px_-2px_rgba(0,0,0,0.06),0_4px_8px_-2px_rgba(0,0,0,0.10)]',
          // Typography — Caption/High emphasis token.
          'text-caption-high text-primary',
          // Interaction
          'hover:bg-secondary transition-colors',
        )}
      >
        <IconHeart className="w-4 h-4 icon-secondary" />
        <span>Personalize your search</span>
        <IconChevronRight className="w-3 h-3 icon-tertiary" />
      </button>

      <RenterProfileWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        screen="home"
      />
    </>
  );
}
